import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { fetchSearchIndex } from '@/api/commandPaletteIndex';
import {
  mergeCommandPaletteIndex,
  readCommandPaletteIndexCache,
  writeCommandPaletteIndexCache,
  type CommandPaletteIndexEntry,
} from '@/utils/commandPaletteIndexDb';

/** Min gap between network syncs while the palette stays open (re-open within session). */
const MIN_SYNC_INTERVAL_MS = 60_000;

export function useCommandPaletteIndex(enabled: boolean) {
  const { user } = useAuth();
  const aid = user?.aid;
  const [items, setItems] = useState<CommandPaletteIndexEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const lastSyncRef = useRef(0);

  const sync = useCallback(async () => {
    if (!aid) return;
    const now = Date.now();
    if (now - lastSyncRef.current < MIN_SYNC_INTERVAL_MS) return;
    lastSyncRef.current = now;
    setLoading(true);
    try {
      const cached = await readCommandPaletteIndexCache(aid);
      const since = cached?.lastSyncAt ?? 0;
      const { serverTime, full, items: fetched } = await fetchSearchIndex(since > 0 ? since : undefined);
      const merged = mergeCommandPaletteIndex(cached?.items ?? [], fetched, full);
      setItems(merged);
      await writeCommandPaletteIndexCache({
        aid,
        lastSyncAt: serverTime,
        items: merged,
      });
    } catch {
      const cached = await readCommandPaletteIndexCache(aid);
      if (cached?.items?.length) setItems(cached.items);
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => {
    if (!aid) {
      setItems([]);
      return;
    }
    if (!enabled) return;

    let cancelled = false;
    void (async () => {
      const cached = await readCommandPaletteIndexCache(aid);
      if (cancelled) return;
      if (cached?.items?.length) setItems(cached.items);
      void sync();
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, aid, sync]);

  return { items, loading, sync };
}
